import { StringMask } from '@/utils/StringMask';
import { NInput } from 'naive-ui';
import { computed, defineComponent, ref, watch } from 'vue';

const MAX_CPF_LENGTH = 11;
const MAX_CNPJ_LENGTH = 14;

export const AppInputDocument = defineComponent({
	name: 'AppInputDocument',
	extends: NInput,

	props: {
		modelValue: String,
	},
	emits: ['update:modelValue', 'update:value', 'change', 'documentTypeDetected'],

	setup(props, { emit }) {
		const cpfMask = new StringMask({ mask: '000.000.000-00' });
		const cnpjMask = new StringMask({ mask: '00.000.000/0000-00' });
		
		const maskedValue = ref(
			props.modelValue?.toString() ? applyMask(props.modelValue.toString()) : '',
		);

		const detectedType = ref<'cpf' | 'cnpj' | null>(null);

		function detectDocumentType(unmaskedValue: string): 'cpf' | 'cnpj' | null {
			const digits = unmaskedValue.replace(/\D/g, '');
			
			if (digits.length <= MAX_CPF_LENGTH) {
				return 'cpf';
			} else if (digits.length <= MAX_CNPJ_LENGTH) {
				return 'cnpj';
			}
			
			return null;
		}

		function applyMask(value: string): string {
			const digits = value.replace(/\D/g, '');
			const type = detectDocumentType(digits);
			
			if (type === 'cpf') {
				return cpfMask.apply(digits).maskedValue;
			} else if (type === 'cnpj') {
				return cnpjMask.apply(digits).maskedValue;
			}
			
			return digits;
		}

		function handleInput(value: string) {
			value = value.toString();
			const digits = value.replace(/\D/g, '');
			const type = detectDocumentType(digits);
			
			let response;
			if (type === 'cpf') {
				response = cpfMask.apply(digits);
			} else if (type === 'cnpj') {
				response = cnpjMask.apply(digits);
			} else {
				// Se não detectou tipo, limita a entrada
				const limitedDigits = digits.slice(0, MAX_CNPJ_LENGTH);
				response = cnpjMask.apply(limitedDigits);
			}
			
			maskedValue.value = response.maskedValue;
			
			// Detecta o tipo quando o documento está completo
			const newType = digits.length === MAX_CPF_LENGTH ? 'cpf' : 
			               digits.length === MAX_CNPJ_LENGTH ? 'cnpj' : null;
			
			if (newType && newType !== detectedType.value) {
				detectedType.value = newType;
				emit('documentTypeDetected', newType);
			}
			
			if (response.unmaskedValue.length != 0) {
				emit('update:modelValue', response.maskedValue);
				emit('update:value', response.maskedValue);
			}
		}

		function onChange() {
			emit('change', maskedValue.value);
		}

		const placeholder = computed(() => {
			if (detectedType.value === 'cpf') {
				return '000.000.000-00';
			} else if (detectedType.value === 'cnpj') {
				return '00.000.000/0000-00';
			}
			return 'CPF ou CNPJ';
		});

		const maxLength = computed(() => {
			if (detectedType.value === 'cpf') {
				return MAX_CPF_LENGTH;
			} else if (detectedType.value === 'cnpj') {
				return MAX_CNPJ_LENGTH;
			}
			return MAX_CNPJ_LENGTH; // Permite até CNPJ enquanto digita
		});

		watch(() => props.modelValue, (newValue) => {
			if (newValue) {
				const digits = newValue.replace(/\D/g, '');
				const type = digits.length === MAX_CPF_LENGTH ? 'cpf' : 
				             digits.length === MAX_CNPJ_LENGTH ? 'cnpj' : null;
				detectedType.value = type;
				maskedValue.value = applyMask(newValue);
			} else {
				maskedValue.value = '';
				detectedType.value = null;
			}
		});

		return () => (
			<NInput
				placeholder={placeholder.value}
				inputProps={{
					autofocus: true,
					maxlength: maxLength.value + 4, // +4 para caracteres de formatação
					autocomplete: 'off',
					inputmode: 'numeric',
				}}
				onChange={onChange}
				onInput={handleInput}
				value={maskedValue.value}
			/>
		);
	},
});

export default AppInputDocument;

